import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  InputAdornment,
  Alert,
} from "@mui/material";
import { updateClass } from "../../api/backend";
import ClassCertificatesPanel from "../../components/certificates/ClassCertificatesPanel";
import AhaSetupCard from "../../components/aha/AhaSetupCard";
import { useTenant } from "../../contexts/TenantContext";
import { useConfigList } from "../../hooks/useConfigList";
import { useTheme, alpha } from "@mui/material/styles";
import { AHA_TOKENS as T } from "../../components/aha/ahaTokens";

const D = {
  ink:  "var(--color-ink)",
  ink2: "rgba(31,22,51,0.75)",
  ink3: "rgba(31,22,51,0.6)",
  ink4: "rgba(31,22,51,0.5)",
  line:  "var(--color-hairline-cloud)",
  line2: "var(--color-hairline-cloud)",
  surface:  "var(--color-surface-canvas-light)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
  danger:    "var(--color-danger)",
  danger50:  "var(--color-danger-bg)",
  danger100: "var(--color-danger-bg)",
  shadowSm: "var(--shadow-card-sm)",
};

const DEFAULT_CATEGORIES = [
  "Firefighter","EMS","Police","Rescue","Lifeguard","Security","ADMIN","AHA","TACTICAL","Other",
];

// ── Shared sub-components ─────────────────────────────────────────────────────

function SvgIcon({ size = 15, children }) {
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </Box>
  );
}

function FlLabel({ children, optional }) {
  return (
    <Box sx={{ fontSize: "13px", fontWeight: 600, color: D.ink, display: "flex", gap: "6px", alignItems: "baseline" }}>
      {children}
      {optional && <Box component="span" sx={{ fontWeight: 400, color: T.muted }}>Optional</Box>}
    </Box>
  );
}

function getControlSx(tPrimary) {
  return {
    "& .MuiOutlinedInput-root": {
      height: 44, borderRadius: "12px", bgcolor: D.surface2,
      "& fieldset": { borderColor: D.line, borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: D.ink3 },
      "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px",
        boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.06)}` },
      "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
    },
    "& .MuiInputBase-input": { fontSize: "14px", fontWeight: 500, color: D.ink },
  };
}

function getMultilineSx(tPrimary) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px", bgcolor: D.surface2,
      "& fieldset": { borderColor: D.line, borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: D.ink3 },
      "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px",
        boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.06)}` },
      "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
    },
    "& .MuiInputBase-input": { fontSize: "14px", fontWeight: 500, color: D.ink, lineHeight: 1.65 },
  };
}

function getSelectSx(tPrimary) {
  return {
    height: 44, borderRadius: "12px", bgcolor: D.surface2, fontSize: "14px", fontWeight: 500, color: D.ink,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: D.line, borderWidth: "1.5px" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: D.ink3 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tPrimary, borderWidth: "1.5px",
      boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.06)}` },
    "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
  };
}

function ToggleRow({ title, sub, checked, onChange, disabled }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "16px", alignItems: "center" }}>
      <Box>
        <Box component="b" sx={{ display: "block", fontSize: 15, fontWeight: 600, color: D.ink }}>{title}</Box>
        {sub && <Box component="span" sx={{ fontSize: "13.5px", color: T.muted }}>{sub}</Box>}
      </Box>
      <Switch checked={checked} onChange={onChange} disabled={disabled} inputProps={{ "aria-label": title }}
        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: tPrimary }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: tPrimary } }} />
    </Box>
  );
}

function CheckIcon({ size = 14 }) {
  return <SvgIcon size={size}><polyline points="20 6 9 17 4 12" /></SvgIcon>;
}

function Field({ label, optional, hint, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
      <FlLabel optional={optional}>{label}</FlLabel>
      {children}
      {hint && <Box sx={{ fontSize: "13px", color: T.muted, lineHeight: 1.4 }}>{hint}</Box>}
    </Box>
  );
}

function Section({ id, index, title, sub, edited, danger, children }) {
  return (
    <Box component="section" id={`cd-${id}`} aria-labelledby={`cd-${id}-h`}
      sx={{ bgcolor: "#fff", border: `1px solid ${danger ? "rgba(180,35,24,.25)" : D.line}`, borderRadius: "12px",
        boxShadow: D.shadowSm, scrollMarginTop: "20px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr) auto", gap: "14px", alignItems: "center", p: "20px 24px" }}>
        <Box component="span" sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700,
          border: `1px solid ${danger ? "rgba(180,35,24,.35)" : T.hairlineCool}`,
          bgcolor: !danger && !edited ? T.success : "#fff", color: danger ? T.danger : edited ? D.ink : "#fff",
          borderColor: !danger && !edited ? T.success : undefined }}>
          {danger
            ? <SvgIcon size={15}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></SvgIcon>
            : edited ? index + 1 : <CheckIcon size={15} />}
        </Box>
        <Box>
          <Box component="h2" id={`cd-${id}-h`} sx={{ m: 0, fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, lineHeight: 1.25, color: danger ? T.danger : D.ink }}>{title}</Box>
          <Box component="p" sx={{ m: "3px 0 0", fontSize: 14, color: T.muted }}>{sub}</Box>
        </Box>
        {edited && (
          <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, px: "10px", height: 24, display: "inline-flex", alignItems: "center", borderRadius: 999,
            color: T.warn, bgcolor: T.warnBg, border: "1px solid rgba(194,65,12,.2)" }}>Unsaved</Box>
        )}
      </Box>
      <Box sx={{ p: { xs: "4px 24px 24px", md: "4px 24px 24px 70px" }, display: "flex", flexDirection: "column", gap: "20px" }}>{children}</Box>
    </Box>
  );
}

const SECTION_KEYS = {
  info: ["title", "category", "totalHours", "description", "prereqs"],
  awards: [],
  enroll: ["availableSeats", "waitlistEnabled", "useGenericTerms", "termsConditions"],
  price: ["feeMode", "feeAmount"],
  vis: ["visibility"],
};

const FEE_MODES = [
  ["FREE_ALL", "Free for everyone"],
  ["PAID_ALL", "Paid for everyone"],
  ["FREE_IN_AGENCY_PAID_OUT", "Free for agency members / Paid for others"],
];
const VISIBILITY = [
  ["TENANT_ONLY", "Tenant only", "Visible only to members of your training center."],
  ["PUBLIC_ONLY", "Public only", "Visible on the public class listing."],
  ["BOTH", "Both", "Visible to members and on the public class listing."],
];

const same = (a, b) => String(a ?? "") === String(b ?? "");

// ── Main component ────────────────────────────────────────────────────────────
/** What a class made from a standard course may not change: they are the course's content, set by Training Center. */
const STANDARD_CONTENT_FIELDS = ["title", "courseNumber", "category", "totalHours", "description", "prereqs"];

/** @param contentLocked the class came from a standard course: its content fields are read-only (seats, terms and fees are not) */
export default function CoordinatorDetailsTab({ course, canEdit, isDraft = false, onPublish, onDelete, contentLocked = false }) {
  const { id } = useParams();
  const { user, isFeatureEnabled } = useTenant();
  const CATEGORIES = useConfigList("COURSE_CATEGORY", DEFAULT_CATEGORIES);
  const showPublicListing = isFeatureEnabled ? isFeatureEnabled("publicClassListing") : false;
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const controlSx = getControlSx(tPrimary);
  const multilineSx = getMultilineSx(tPrimary);
  const selectSx = getSelectSx(tPrimary);

  const [form, setForm] = useState({});
  // The last saved values: what "unsaved" is measured against, and what Discard goes back to.
  const [saved, setSaved] = useState({});
  // Bumped when the class's certificates change, so the AHA product section re-reads whether it applies.
  const [certificatesVersion, setCertificatesVersion] = useState(0);
  // Bumped when the AHA section switches AHA on or off, so the certificates list re-reads what is saved.
  const [ahaVersion, setAhaVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("info");
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (course) {
      const initial = {
        title: course.title || "",
        courseNumber: course.courseNumber || "",
        category: course.category || "",
        availableSeats: course.availableSeats ?? 20,
        totalHours: course.totalHours ?? 0,
        description: course.description || "",
        prereqs: course.prereqs && course.prereqs !== "None" ? course.prereqs : "",
        waitlistEnabled: course.waitlistEnabled === true,
        useGenericTerms: course.useGenericTerms !== false,
        termsConditions: course.termsConditions || "",
        feeMode: course.feeMode || "FREE_ALL",
        feeAmount: course.feeAmount ?? 0,
        visibility: course.visibility || "TENANT_ONLY",
      };
      setForm(initial);
      setSaved(initial);
    }
  }, [course]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setBool = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.checked }));

  const sections = [
    { id: "info", title: "Course information", sub: "What students see in the class listing." },
    { id: "awards", title: "What students receive", sub: "Certificates and AHA eCards issued when a student passes." },
    { id: "enroll", title: "Enrollment", sub: "Seats, waitlist and terms students accept when they register." },
    { id: "price", title: "Pricing", sub: "What registration costs." },
    ...(showPublicListing ? [{ id: "vis", title: "Who can find this class", sub: "Applies once the class is published." }] : []),
  ];
  const isDirty = (sid) => SECTION_KEYS[sid].some((k) => !same(form[k], saved[k]));
  const dirty = sections.filter((s) => isDirty(s.id));
  const ready = sections.length - dirty.length;

  const summary = {
    info: [form.category, `${form.totalHours || 0} hours`].filter(Boolean).join(" · "),
    awards: "Certificates and AHA eCards",
    enroll: `${form.availableSeats === "" ? "Unlimited" : form.availableSeats} seats · waitlist ${form.waitlistEnabled ? "on" : "off"}`,
    price: form.feeMode === "FREE_ALL" ? "Free for everyone" : `$${Number(form.feeAmount || 0).toFixed(2)}`,
    vis: (VISIBILITY.find(([v]) => v === form.visibility) || VISIBILITY[0])[1],
  };

  const go = (sid) => {
    setActive(sid);
    const el = document.getElementById(`cd-${sid}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const save = async () => {
    setErr(null);
    if (!form.title) { setErr("Course title is required."); return; }
    if (!user?.tenantId) { setErr("No active tenant — cannot save."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        prereqs: form.prereqs || "None",
        feeAmount: parseFloat(form.feeAmount) || 0,
        totalHours: parseInt(form.totalHours, 10) || 0,
      };
      // The server refuses a standard class's content fields, even unchanged: send only what a center may change.
      if (contentLocked) STANDARD_CONTENT_FIELDS.forEach((field) => delete payload[field]);
      await updateClass(user?.tenantId, id, payload);
      setSaved(form);
    } catch (e) {
      setErr(e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (!course) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress sx={{ color: tPrimary }} />
    </Box>
  );

  const paid = form.feeMode !== "FREE_ALL";
  const anyDirty = dirty.length > 0;
  const status = isDraft ? "Draft" : "Published";
  const heading = anyDirty ? `${ready} of ${sections.length} sections saved` : isDraft ? "Ready to publish" : "All changes saved";
  const numbered = (sid) => sections.findIndex((s) => s.id === sid);

  return (
    <Box>
      {err && (
        <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "12px" }}>
          {err}
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) 300px" }, gap: "28px", alignItems: "start", pb: anyDirty ? 10 : 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", minWidth: 0 }}>
          {/* ── 1. Course information ───────────────────────────────────── */}
          <Section id="info" index={numbered("info")} title={sections[0].title} sub={sections[0].sub} edited={isDirty("info")}>
            <Field label="Course name">
              <TextField fullWidth size="small" value={form.title || ""} onChange={set("title")} disabled={!canEdit || contentLocked}
                inputProps={{ "aria-label": "Course name" }} sx={controlSx} />
            </Field>
            <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0,1fr))" } }}>
              <Field label="Course code" hint="Set when the course was created.">
                <Box sx={{ height: 44, display: "flex", alignItems: "center", gap: "8px", px: "14px", borderRadius: "6px", bgcolor: D.surface2,
                  fontFamily: '"JetBrains Mono", monospace', fontSize: "13.5px", minWidth: 0, overflow: "hidden" }}>
                  <Box sx={{ color: T.muted, display: "flex" }}>
                    <SvgIcon size={14}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></SvgIcon>
                  </Box>
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.courseNumber || "—"}</Box>
                </Box>
              </Field>
              <Field label="Category">
                <Select fullWidth size="small" value={form.category || ""} onChange={set("category")} disabled={!canEdit || contentLocked}
                  displayEmpty SelectDisplayProps={{ "aria-label": "Category" }}
                  renderValue={(v) => v || <span style={{ color: D.ink4 }}>Select a category</span>} sx={selectSx}>
                  <MenuItem value=""><em style={{ color: D.ink4 }}>Select a category</em></MenuItem>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </Field>
              <Field label="Total hours">
                <TextField fullWidth size="small" type="number" inputProps={{ min: 0, "aria-label": "Total hours" }} value={form.totalHours ?? 0}
                  onChange={set("totalHours")} disabled={!canEdit || contentLocked} sx={controlSx} />
              </Field>
            </Box>
            <Field label="Description" optional>
              <TextField fullWidth multiline rows={3} placeholder="Outline the learning objectives and course overview…"
                value={form.description || ""} onChange={set("description")} disabled={!canEdit || contentLocked}
                inputProps={{ "aria-label": "Description" }} sx={multilineSx} />
            </Field>
            <Field label="Prerequisites" optional hint="Students see this before they register.">
              <TextField fullWidth size="small" placeholder="e.g. Firefighter I, NFPA 1001 Basic"
                value={form.prereqs || ""} onChange={set("prereqs")} disabled={!canEdit || contentLocked}
                inputProps={{ "aria-label": "Prerequisites" }} sx={controlSx} />
            </Field>
          </Section>

          {/* ── 2. What students receive (certificates and AHA save themselves) ─ */}
          <Section id="awards" index={numbered("awards")} title={sections[1].title} sub={sections[1].sub} edited={false}>
            {/* AHA eCards are set up in their own checklist below, so this list doesn't offer them. */}
            <ClassCertificatesPanel classId={id} canEdit={canEdit && !contentLocked} excludeTypes={["AHA"]} refreshKey={ahaVersion}
              onChanged={() => setCertificatesVersion((v) => v + 1)} />
            {/* AHA is part of a standard course: its setup is shown, never edited here (AHA comes from Use this course). */}
            {contentLocked && (
              <AhaSetupCard classId={id} canEdit={false} standard refreshKey={certificatesVersion}
                onChanged={() => setAhaVersion((v) => v + 1)} />
            )}
          </Section>

          {/* ── 3. Enrollment ───────────────────────────────────────────── */}
          <Section id="enroll" index={numbered("enroll")} title={sections[2].title} sub={sections[2].sub} edited={isDirty("enroll")}>
            <Box sx={{ maxWidth: 240 }}>
              <Field label="Available seats" optional hint="Leave blank for no limit.">
                <TextField fullWidth size="small" type="number" value={form.availableSeats} onChange={set("availableSeats")} disabled={!canEdit}
                  inputProps={{ "aria-label": "Available seats" }} sx={controlSx} />
              </Field>
            </Box>
            <Box sx={{ border: `1px solid ${D.line}`, borderRadius: "10px", "& > *": { p: "14px 16px" }, "& > * + *": { borderTop: `1px solid ${D.line}` } }}>
              <ToggleRow title="Waitlist" sub="Let students join a waitlist when the class is full."
                checked={form.waitlistEnabled === true} onChange={setBool("waitlistEnabled")} disabled={!canEdit} />
              <ToggleRow title="Use generic terms & conditions" sub="Use the standard T&Cs from Training Center settings."
                checked={form.useGenericTerms !== false} onChange={setBool("useGenericTerms")} disabled={!canEdit} />
            </Box>
            {form.useGenericTerms === false && (
              <Field label="Class-specific terms & conditions">
                <TextField fullWidth multiline rows={4}
                  placeholder="Enter the terms and conditions students must accept before registering…"
                  value={form.termsConditions || ""} onChange={set("termsConditions")} disabled={!canEdit}
                  inputProps={{ "aria-label": "Class-specific terms and conditions" }} sx={multilineSx} />
              </Field>
            )}
          </Section>

          {/* ── 4. Pricing ──────────────────────────────────────────────── */}
          <Section id="price" index={numbered("price")} title={sections[3].title} sub={sections[3].sub} edited={isDirty("price")}>
            <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(220px, 1fr))" } }}>
              <Field label="Fee mode" hint={form.feeMode === "PAID_ALL" ? "Every student pays the class price."
                : form.feeMode === "FREE_IN_AGENCY_PAID_OUT" ? "Agency members register free; everyone else pays the class price."
                : "All students register for free."}>
                <Select fullWidth size="small" value={form.feeMode || "FREE_ALL"} onChange={set("feeMode")} disabled={!canEdit}
                  SelectDisplayProps={{ "aria-label": "Fee mode" }} sx={selectSx}>
                  {FEE_MODES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </Select>
              </Field>
              {paid && (
                <Field label="Class price (USD)">
                  <TextField fullWidth size="small" type="number" inputProps={{ min: 0, step: 0.01, "aria-label": "Class price (USD)" }}
                    value={form.feeAmount ?? 0}
                    onChange={(e) => setForm((p) => ({ ...p, feeAmount: parseFloat(e.target.value) || 0 }))}
                    disabled={!canEdit} sx={controlSx}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                </Field>
              )}
            </Box>
          </Section>

          {/* ── 5. Visibility (only where the public listing is on) ──────── */}
          {showPublicListing && (
            <Section id="vis" index={numbered("vis")} title={sections[4].title} sub={sections[4].sub} edited={isDirty("vis")}>
              <Box role="radiogroup" aria-label="Course visibility"
                sx={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                {VISIBILITY.map(([v, t, d]) => {
                  const on = form.visibility === v;
                  return (
                    <Box key={v} component="button" type="button" role="radio" aria-checked={on} disabled={!canEdit}
                      onClick={() => setForm((p) => ({ ...p, visibility: v }))}
                      sx={{ display: "grid", gridTemplateColumns: "20px minmax(0,1fr)", gap: "12px", p: "14px 16px", textAlign: "left", font: "inherit",
                        color: "inherit", cursor: canEdit ? "pointer" : "default", borderRadius: "10px", bgcolor: on ? "rgba(106,95,193,.04)" : "#fff",
                        border: `1px solid ${on ? T.violet : T.hairlineCool}`, boxShadow: on ? `inset 0 0 0 1px ${T.violet}` : "none",
                        "&:hover:not(:disabled)": { bgcolor: on ? "rgba(106,95,193,.04)" : T.subtle } }}>
                      <Box component="span" sx={{ width: 18, height: 18, borderRadius: "50%", mt: "2px", display: "grid", placeItems: "center",
                        border: `1.5px solid ${on ? T.violet : T.hairlineCool}`,
                        "&::after": on ? { content: '""', width: 9, height: 9, borderRadius: "50%", bgcolor: T.violet } : {} }} />
                      <Box>
                        <Box component="b" sx={{ display: "block", fontSize: 15, fontWeight: 600 }}>{t}</Box>
                        <Box component="span" sx={{ fontSize: "13.5px", color: T.muted }}>{d}</Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Section>
          )}

          {/* ── Remove this class ───────────────────────────────────────── */}
          {canEdit && (
            <Section id="danger" danger title="Remove this class" sub="These can’t be undone from this page.">
              <Box sx={{ border: `1px solid ${D.line}`, borderRadius: "10px", "& > *": { p: "14px 16px" }, "& > * + *": { borderTop: `1px solid ${D.line}` } }}>
                <DangerRow title="Archive course" sub="Removes it from the catalog. Enrolled students keep their records." label="Archive" />
                {onDelete && (
                  <DangerRow title="Delete class" sub="Permanently deletes this class and its sessions. Not possible while students are enrolled."
                    label="Delete class" onClick={onDelete} />
                )}
              </Box>
            </Section>
          )}
        </Box>

        {/* ── Progress rail ─────────────────────────────────────────────── */}
        <Box component="aside" sx={{ position: { md: "sticky" }, top: 20, order: { xs: -1, md: 0 } }}>
          <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "12px", boxShadow: D.shadowSm }}>
            <Box sx={{ p: "18px 20px 14px", borderBottom: `1px solid ${D.line}` }}>
              <Box sx={{ fontSize: 10, fontWeight: 600, letterSpacing: ".25px", textTransform: "uppercase", color: T.muted }}>{status}</Box>
              <Box component="h3" sx={{ m: "4px 0 0", fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 600 }}>{heading}</Box>
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: D.surface2, mt: "12px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", bgcolor: T.success, borderRadius: 3, width: `${(ready / sections.length) * 100}%` }} />
              </Box>
            </Box>
            <Box component="nav" aria-label="Sections" sx={{ p: "8px" }}>
              <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {sections.map((s) => {
                  const edit = isDirty(s.id);
                  return (
                    <li key={s.id}>
                      <Box component="button" type="button" onClick={() => go(s.id)}
                        sx={{ display: "grid", gridTemplateColumns: "22px minmax(0,1fr)", gap: "10px", alignItems: "start", width: "100%", textAlign: "left",
                          border: 0, p: "9px 10px", borderRadius: "8px", cursor: "pointer", color: D.ink, font: "inherit",
                          bgcolor: active === s.id ? D.surface2 : "transparent", "&:hover": { bgcolor: D.surface2 } }}>
                        <Box component="span" sx={{ width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", mt: "1px",
                          bgcolor: edit ? T.warnBg : T.success, color: edit ? T.warn : "#fff", border: edit ? "1px solid rgba(194,65,12,.3)" : 0 }}>
                          {edit ? <SvgIcon size={12}><line x1="5" y1="12" x2="19" y2="12" /></SvgIcon> : <CheckIcon size={12} />}
                        </Box>
                        <Box>
                          <Box component="b" sx={{ display: "block", fontSize: 14, fontWeight: 600 }}>{s.title}</Box>
                          <Box component="span" sx={{ fontSize: 12.5, color: T.muted }}>{edit ? "Unsaved changes" : summary[s.id]}</Box>
                        </Box>
                      </Box>
                    </li>
                  );
                })}
              </Box>
            </Box>
            {isDraft && canEdit && onPublish && (
              <Box sx={{ p: "14px 20px 18px", borderTop: `1px solid ${D.line}`, display: "flex", flexDirection: "column", gap: "10px" }}>
                <Box component="button" type="button" onClick={onPublish} disabled={anyDirty}
                  sx={{ height: 38, borderRadius: "11px", border: 0, fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                    bgcolor: anyDirty ? D.surface3 : tPrimary, color: anyDirty ? D.ink3 : "var(--color-on-primary)",
                    cursor: anyDirty ? "default" : "pointer", "&:hover:not(:disabled)": { bgcolor: theme.palette.primary.dark } }}>
                  Publish class
                </Box>
                <Box sx={{ fontSize: 13, color: T.muted, textAlign: "center" }}>
                  {anyDirty ? "Save your changes before publishing." : "Students can register once published."}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Unsaved changes bar ─────────────────────────────────────────── */}
      {canEdit && anyDirty && (
        <Box role="region" aria-label="Unsaved changes"
          sx={{ position: "fixed", left: 0, right: 0, mx: "auto", width: "max-content", maxWidth: "calc(100vw - 32px)", bottom: 24, zIndex: 10,
            display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", p: "12px 12px 12px 20px", borderRadius: "12px",
            bgcolor: T.ink, color: "#fff", boxShadow: "var(--shadow-card-md)", fontSize: 14 }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: T.lime }} />
          <Box component="span" sx={{ flex: "1 1 auto", minWidth: 0 }}>
            Unsaved changes in <b>{dirty.map((s) => s.title).join(", ")}</b>
          </Box>
          <Box component="button" type="button" onClick={() => { setForm(saved); setErr(null); }} disabled={saving}
            sx={{ bgcolor: "transparent", border: "1px solid rgba(255,255,255,.25)", color: "#fff", fontFamily: "inherit", fontSize: 12, fontWeight: 700,
              letterSpacing: ".2px", textTransform: "uppercase", p: "8px 14px", borderRadius: "8px", cursor: "pointer",
              "&:hover": { bgcolor: "rgba(255,255,255,.08)" } }}>
            Discard
          </Box>
          <Box component="button" type="button" onClick={save} disabled={saving || !form.title}
            sx={{ bgcolor: "#fff", color: T.ink, border: 0, fontFamily: "inherit", fontSize: 12, fontWeight: 700, letterSpacing: ".2px",
              textTransform: "uppercase", p: "9px 16px", borderRadius: "8px", cursor: "pointer", "&:disabled": { opacity: 0.6, cursor: "default" } }}>
            {saving ? "Saving…" : "Save changes"}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function DangerRow({ title, sub, label, onClick }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "16px", alignItems: "center" }}>
      <Box>
        <Box component="b" sx={{ display: "block", fontSize: 15, fontWeight: 600 }}>{title}</Box>
        <Box component="span" sx={{ fontSize: "13.5px", color: T.muted }}>{sub}</Box>
      </Box>
      <Box component="button" type="button" onClick={onClick}
        sx={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, letterSpacing: ".2px", textTransform: "uppercase", p: "8px 14px", borderRadius: "8px",
          border: "1px solid rgba(180,35,24,.35)", bgcolor: "#fff", color: T.danger, cursor: "pointer", whiteSpace: "nowrap",
          "&:hover": { bgcolor: T.dangerBg } }}>
        {label}
      </Box>
    </Box>
  );
}
